/* ============================================================================
 * Palette officielle Ki Para — LA SEULE source de couleurs du frontend.
 * (Ki_Para_Palette.html, owner 2026-09-01 — « chaque couleur a un rôle,
 * jamais décorative »)
 *
 * Règle owner : AUCUN code couleur en dur ailleurs dans src/.
 *  - tailwind.config.js  require() ce fichier pour les rampes des classes ;
 *  - les composants JS (graphes, SweetAlert, styles inline) importent KI ;
 *  - src/index.css garde les jetons :root (le CSS ne peut pas importer du JS),
 *    ils doivent rester alignés sur ce fichier.
 * Une nouvelle couleur entre ICI (envoyée par Ahmed), jamais dans un composant.
 * ========================================================================== */

/* Rampes Tailwind (du clair 50 au foncé 900) — remap global : tout le site
 * bascule sur la palette sans toucher les composants. */
const gray = { 50: '#F9FAFB', 100: '#F3F4F6', 200: '#E5E7EB', 300: '#D1D5DB', 400: '#9CA3AF', 500: '#6B7280', 600: '#4B5563', 700: '#374151', 800: '#1F2937', 900: '#111827' };
/* Vert : 700 = #15803D primaire du comptoir (Encaisser, Payer, Vendre),
 * 600 = #16A34A états acquis / Points / TRI ACTIF, 900 = survol du primaire.
 * 150/650 = alias de compat pour les classes existantes. */
const green = { 50: '#F0FDF4', 100: '#DCFCE7', 150: '#DCFCE7', 200: '#BBF7D0', 300: '#86EFAC', 400: '#4ADE80', 500: '#22C55E', 600: '#16A34A', 650: '#16A34A', 700: '#15803D', 800: '#166534', 850: '#14532D', 900: '#14532D' };
/* Bleu : ce qui se déclenche ou se signale (Marquer arrivée, Réservable,
 * liens Ajouter, Nouveau produit, série Achats). sky = alias du bleu. */
const blue = { 50: '#EFF6FF', 100: '#DBEAFE', 200: '#BFDBFE', 250: '#93C5FD', 300: '#93C5FD', 400: '#60A5FA', 500: '#3B82F6', 600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A' };
const sky = blue;
/* Rouge : problèmes et remises — le SEUL aplat critique du site. */
const red = { 50: '#FEF2F2', 100: '#FEE2E2', 200: '#FECACA', 300: '#FCA5A5', 400: '#F87171', 500: '#EF4444', 600: '#DC2626', 700: '#B91C1C', 800: '#991B1B', 900: '#7F1D1D' };
const amber = { 50: '#FFFBEB', 100: '#FEF3C7', 200: '#FDE68A', 300: '#FCD34D', 400: '#FBBF24', 500: '#F59E0B', 600: '#D97706', 700: '#B45309', 800: '#92400E', 900: '#78350F' };
/* Gris d'interface documentés par la palette (nav par jour, pagination,
 * en-tête de tableau, lien « Voir › ») — hors rampe typographique. */
const iface = { 100: '#e9e9ea', 150: '#F5F5F8', 300: '#b7b7ba', 500: '#98989b', 600: '#5d5d60', 700: '#424244', 900: '#2b2b2d' };

const ramps = {
  white: '#FFFFFF',
  gray, slate: gray,
  green, emerald: green,
  sky, teal: green,
  blue,
  red, rose: red, orange: amber,
  amber, yellow: amber,
  iface,
  night: '#0A0A0A',      /* fond sombre mobile, FAB notes-chat, Actions rapides */
  goldtext: '#76631B',   /* texte du badge Score (doc étiquettes) */
  goldline: '#6D591B',   /* bordure du badge Charge (journal) */
};

/* Rôles nommés — à préférer dans le code JS quand le sens est connu.
 * (Les anciens noms Clinique sont conservés et re-pointés : le kit
 * 2026-09-04 redéfinit chaque rôle sur la palette Ki Para classique.) */
const KI = {
  /* rôles */
  peacock: gray[900],       /* #111827 — boutons qui ENREGISTRENT (noir encre) */
  spruce: green[700],       /* #15803D — primaire comptoir (Encaisser, Payer, Vendre) */
  stateGreen: green[600],   /* #16A34A — états acquis, Points, TRI ACTIF, série CA */
  sapphire: blue[600],      /* #2563EB — se déclenche / se signale (bleu) */
  /* rouge (alertes) */
  coral: red[400],          /* #F87171 — bordés Soin, alerte claire */
  coralDark: red[600],      /* #DC2626 — aplat rupture/impayé (texte blanc), danger */
  coralText: red[700],      /* #B91C1C — texte rouge sur fond clair */
  /* amber (fidélité / vigilance) */
  gold: amber[500],         /* #F59E0B — début du dégradé FIDÈLE GOLD */
  goldDark: amber[600],     /* #D97706 — promos, packs, fin du dégradé */
  goldText: amber[700],     /* #B45309 — texte AA sur fond clair */
  /* neutres */
  ink: gray[900],           /* #111827 — texte principal, chrome, toasts */
  muted: gray[500],         /* #6B7280 — texte secondaire */
  faint: gray[400],         /* #9CA3AF — détails, placeholders */
  border: gray[300],        /* #D1D5DB — bordures de champ */
  borderLight: gray[200],   /* #E5E7EB — bordures de carte */
  pageBg: gray[100],        /* #F3F4F6 — fond de page */
  surface: ramps.white,     /* #FFFFFF — cartes, tableaux */
  /* thème sombre (mobile) — kit 2026-09-04 §06 */
  darkBg: '#0A0A0A',
  darkCard: gray[800],      /* #1F2937 */
  darkBorder: gray[700],    /* #374151 */
  /* frame imposé (header/footer) — jamais recoloré */
  frame: '#111827',
  black: '#000000',
  pureWhite: '#FFFFFF',
  ramps,
};

/* rgba() d'une couleur de la palette — pour les voiles/ombres. */
KI.alpha = function (hex, a) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

module.exports = KI;
module.exports.ramps = ramps;
module.exports.KI = KI;
